import { lazy, Suspense, useEffect } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes, Navigate, useParams } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import MetaPixelInit from "@/components/MetaPixelInit";
import { CookieBanner } from "@/components/CookieBanner";
import { IOSInstallBanner } from "@/components/IOSInstallBanner";
import PostHogInit from "@/components/PostHogInit";
import SignupTracker from "@/components/SignupTracker";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { ScrollToTop } from "@/components/ScrollToTop";
import * as Sentry from "@sentry/react";
// Eager — entry points that must load immediately
import Landing from "./pages/Landing";
import SignIn from "./pages/SignIn";
import NotFound from "./pages/NotFound";
// Lazy — heavy pages loaded only when needed
const DashboardRoute = lazy(() => import("./pages/DashboardRoute"));
const PublicMenu = lazy(() => import("./pages/PublicMenu"));
const PreviewMenu = lazy(() => import("./pages/PreviewMenu"));
const Pricing = lazy(() => import("./pages/Pricing"));
const Checkout = lazy(() => import("./pages/Checkout"));
const CheckoutSuccess = lazy(() => import("./pages/CheckoutSuccess"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const Funcionalidades = lazy(() => import("./pages/Funcionalidades"));
const Sobre = lazy(() => import("./pages/Sobre"));
const Contact = lazy(() => import("./pages/Contact"));
const AdminLayout = lazy(() => import("@/components/admin/AdminLayout"));
const AdminOverview = lazy(() => import("./pages/admin/AdminOverview"));
const AdminRestaurants = lazy(() => import("./pages/admin/AdminRestaurants"));
const AdminPartners = lazy(() => import("./pages/admin/AdminPartners"));
const AdminRestaurantDetail = lazy(() => import("./pages/admin/AdminRestaurantDetail"));
const AdminSupportRequests = lazy(() => import("./pages/admin/AdminSupportRequests"));
const AdminPreviews = lazy(() => import("./pages/admin/AdminPreviews"));
const AdminPreviewNew = lazy(() => import("./pages/admin/AdminPreviewNew"));
const ZaiFlowPreview = import.meta.env.DEV ? lazy(() => import("./pages/ZaiFlowPreview")) : null;
const ManualFlowPreview = import.meta.env.DEV ? lazy(() => import("./pages/ManualFlowPreview")) : null;
const DashboardActivationPreview = import.meta.env.DEV ? lazy(() => import("./pages/DashboardActivationPreview")) : null;

const queryClient = new QueryClient();

const MenuRedirect = () => {
  const { slug } = useParams();
  return <Navigate to={`/${slug || ""}`} replace />;
};

const AppContent = () => {
  // Register Service Worker for PWA support
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;

    if (import.meta.env.DEV) {
      void navigator.serviceWorker
        .getRegistrations()
        .then((registrations) => Promise.all(registrations.map((registration) => registration.unregister())))
        .catch((error) => console.warn("Service Worker cleanup failed:", error));
      return;
    }

    const register = async () => {
      try {
        await navigator.serviceWorker.register("/sw.js");
      } catch (error) {
        console.warn("Service Worker registration failed:", error);
      }
    };
    void register();
  }, []);

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/entrar" element={<SignIn />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/funcionalidades" element={<Funcionalidades />} />
      <Route path="/sobre" element={<Sobre />} />
      <Route path="/contato" element={<Contact />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/checkout" element={<Checkout />} />
      <Route path="/checkout/success" element={<CheckoutSuccess />} />
      {ZaiFlowPreview && <Route path="/preview-zai" element={<ZaiFlowPreview />} />}
      {ManualFlowPreview && <Route path="/preview-manual" element={<ManualFlowPreview />} />}
      {DashboardActivationPreview && <Route path="/preview-activation" element={<DashboardActivationPreview />} />}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardRoute />
          </ProtectedRoute>
        }
      />
      <Route path="/menu/:slug" element={<MenuRedirect />} />
      <Route path="/preview/:slug" element={<PreviewMenu />} />
      <Route path="/:slug" element={<PublicMenu />} />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<AdminOverview />} />
        <Route path="restaurants" element={<AdminRestaurants />} />
        <Route path="partners" element={<AdminPartners />} />
        <Route path="restaurants/:userId" element={<AdminRestaurantDetail />} />
        <Route path="support" element={<AdminSupportRequests />} />
        <Route path="previews" element={<AdminPreviews />} />
        <Route path="previews/new" element={<AdminPreviewNew />} />
      </Route>
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

const App = () => (
  <Sentry.ErrorBoundary fallback={<ErrorBoundary><div /></ErrorBoundary>} showDialog={false}>
    <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <ScrollToTop />
          <AuthProvider>
            <MetaPixelInit />
            <PostHogInit />
            <SignupTracker />
            <CookieBanner />
            <IOSInstallBanner />
            <Suspense fallback={null}>
              <AppContent />
            </Suspense>
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </ErrorBoundary>
  </Sentry.ErrorBoundary>
);

export default App;
